import React, { useState } from "react";
import { useUI } from "../../context/UIContext";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { showToast } = useUI();
  const { login } = useAuth();
  const navigate = useNavigate();

  // Pre-fill email and remember choice if previously saved
  React.useEffect(() => {
    const savedEmail = localStorage.getItem('remembered_email');
    if (savedEmail) {
      setEmail(savedEmail);
      setRemember(true);
    }
    
    // Matomo page tracking
    if (window._paq) {
      window._paq.push(['setCustomUrl', window.location.pathname]);
      window._paq.push(['setDocumentTitle', 'Connexion']);
      window._paq.push(['trackPageView']);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      });
      const data = await response.json();
      console.log("[LoginDebug] Raw data from server:", data);
      if (!response.ok) {
        const errorMessage = data.error || "Erreur de connexion";
        setError(errorMessage);
        showToast(errorMessage, 'error');
      } else {
        setError("");
        showToast("Connexion réussie !", 'success');

        // Handle Remember Me for credentials
        if (remember) {
          localStorage.setItem('remembered_email', email);
        } else {
          localStorage.removeItem('remembered_email');
        }

        login(data, remember);

        // Détection mobile via User-Agent (plus fiable que la simple résolution)
        const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;

        if (isMobileDevice) {
          navigate("/mobile");
        } else {
          navigate("/dashboard");
        }
      }
    } catch (err) {
      setError("Erreur serveur");
      showToast("Erreur serveur", 'error');
    }
    setLoading(false);
  };

  return (
    <div className="bg-background-light dark:bg-background-dark min-h-screen flex flex-col font-display transition-colors duration-300">
      <div className="relative flex min-h-screen w-full flex-col items-center justify-center bg-gradient-mesh px-4">
        <div className="w-full max-w-[440px] flex flex-col items-center">
          <div className="mb-10 flex flex-col items-center gap-2">
            <div className="size-24 bg-white dark:bg-slate-900 rounded-3xl flex items-center justify-center border border-slate-200 dark:border-slate-800 shadow-xl shadow-primary/5 p-4">
              <img src="/Stockizio.png" alt="Stockizio Logo" className="w-full h-full object-contain" />
            </div>
            <h1 className="text-slate-900 dark:text-slate-100 text-4xl font-black tracking-tighter mt-6">
              Stock<span className="text-primary">izio</span>
            </h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm font-medium uppercase tracking-widest mt-1 opacity-80">Gérez votre inventaire en toute simplicité</p>
          </div>

          <div className="w-full bg-white dark:bg-slate-900/80 backdrop-blur-xl p-8 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mb-8">Connexion</h2>

            {error && <div className="mb-4 text-[#ec5b13] text-sm font-semibold text-center">{error}</div>}

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="flex flex-col gap-2">
                <label className="text-slate-700 dark:text-slate-300 text-sm font-medium">Identifiant</label>
                <div className="relative group">
                  <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors">person</span>
                  <input
                    className="w-full pl-12 pr-4 h-14 bg-slate-50 dark:bg-[#261b16] border border-slate-200 dark:border-primary/20 rounded-xl text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-primary focus:border-transparent transition-all outline-none placeholder:text-slate-400 dark:placeholder:text-slate-600"
                    placeholder="votre@email.com"
                    type="text"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex justify-between items-center">
                  <label className="text-slate-700 dark:text-slate-300 text-sm font-medium">Mot de passe</label>
                  <span
                    className="text-primary text-sm font-medium hover:underline cursor-pointer"
                    onClick={() => navigate('/forgot-password')}
                  >
                    Mot de passe oublié ?
                  </span>
                </div>
                <div className="relative group">
                  <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors">lock</span>
                  <input
                    className="w-full pl-12 pr-12 h-14 bg-slate-50 dark:bg-[#261b16] border border-slate-200 dark:border-primary/20 rounded-xl text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-primary focus:border-transparent transition-all outline-none placeholder:text-slate-400 dark:placeholder:text-slate-600"
                    placeholder="••••••••"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                  />
                  <button
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <span className="material-symbols-outlined">{showPassword ? "visibility" : "visibility_off"}</span>
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3 py-2">
                <input
                  className="size-5 rounded border-slate-300 dark:border-primary/30 text-primary focus:ring-primary bg-transparent"
                  id="remember"
                  type="checkbox"
                  checked={remember}
                  onChange={e => setRemember(e.target.checked)}
                />
                <label className="text-slate-600 dark:text-slate-400 text-sm cursor-pointer" htmlFor="remember">Se souvenir de moi</label>
              </div>

              <button
                className="w-full h-14 bg-primary hover:bg-orange-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-primary/30 active:scale-[0.98] outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:focus:ring-offset-slate-900"
                disabled={loading}
              >
                {loading ? "Connexion..." : "Se connecter"}
              </button>
            </form>

            {/* <div className="mt-8 pt-6 border-t border-slate-100 dark:border-primary/10 text-center">
              <p className="text-slate-500 dark:text-slate-400 text-sm">
                Besoin d'un compte ? <a className="text-primary font-semibold hover:underline" href="mailto:contact@jobizio.fr">Contactez l'administrateur</a>
              </p>
            </div> */}
          </div>

          <div className="mt-8 flex gap-6 text-slate-400 dark:text-slate-600">
            <a
              className="text-xs hover:text-primary transition-colors uppercase tracking-widest font-bold cursor-pointer"
              onClick={() => navigate('/help')}
            >Les fonctionnalités</a>
            {/* <a
              className="text-xs hover:text-primary transition-colors uppercase tracking-widest font-bold cursor-pointer"
              onClick={(e) => { e.preventDefault(); showToast("Section Sécurité en cours de rédaction.", "info"); }}
            >Sécurité</a>
            <a
              className="text-xs hover:text-primary transition-colors uppercase tracking-widest font-bold cursor-pointer"
              onClick={(e) => { e.preventDefault(); showToast("Politique de confidentialité interne à votre entreprise.", "info"); }}
            >Confidentialité</a> */}
          </div>
        </div>

        <div className="fixed bottom-6 right-6">
          <button className="size-10 rounded-full bg-white dark:bg-primary/20 flex items-center justify-center border border-slate-200 dark:border-primary/30 text-slate-600 dark:text-primary hover:scale-110 transition-transform shadow-lg">
            <span className="material-symbols-outlined">help_outline</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;
